import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Tabs, Tab, Grid, Card, CardContent } from '@mui/material';
import {
    AddCircleOutline as AddCircleOutlineIcon,
    Schedule as ScheduleIcon,
    HourglassEmpty as PendingIcon,
    CheckCircleOutline as ApprovedIcon,
    CancelOutlined as RejectedIcon,
    AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import TimesheetList from './timesheetList';
import TimesheetForm from './timesheetForm';
import { getData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';
import { getUserData } from '../../../utils/auth';
import Loader from '../../../common/spinner';

const DEFAULT_STATS = {
    totalHours: 0,
    pendingCount: 0,
    approvedCount: 0,
    rejectedCount: 0,
};

function MyTimesheet() {
    const [stats, setStats] = useState(DEFAULT_STATS);
    const [statsLoading, setStatsLoading] = useState(true);
    const [statsError, setStatsError] = useState(null);

    const fetchStats = useCallback(async () => {
        const userData = getUserData();
        const userId = userData?.id;
        if (!userId) {
            setStats(DEFAULT_STATS);
            setStatsLoading(false);
            return;
        }

        setStatsLoading(true);
        setStatsError(null);

        try {
            const res = await getData(API_ENDPOINTS.getTimesheetStats(userId));
            if (res?.status === 'SUCCESS') {
                const payload = res.result?.data ?? {};
                setStats({
                    totalHours: payload.totalHoursThisMonth ?? 0,
                    pendingCount: payload.pendingCount ?? 0,
                    approvedCount: payload.approvedCount ?? 0,
                    rejectedCount: payload.rejectedCount ?? 0,
                });
            } else {
                setStats(DEFAULT_STATS);
                setStatsError(res?.message || 'Unable to fetch stats');
            }
        } catch (fetchError) {
            setStats(DEFAULT_STATS);
            setStatsError(fetchError?.message || 'Unable to fetch stats');
        } finally {
            setStatsLoading(false);
        }
    }, []);

    useEffect(() => { fetchStats(); }, [fetchStats]);

    const STAT_CARDS = [
        { label: 'Total Hours This Month', value: stats.totalHours, suffix: 'hrs', icon: ScheduleIcon, gradient: 'linear-gradient(135deg, #818CF8 0%, #6366F1 100%)' },
        { label: 'Pending Approvals',       value: stats.pendingCount,  icon: PendingIcon,  gradient: 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)' },
        { label: 'Approved Timesheets',     value: stats.approvedCount,  icon: ApprovedIcon,  gradient: 'linear-gradient(135deg, #34D399 0%, #10B981 100%)' },
        { label: 'Rejected Timesheets',      value: stats.rejectedCount,  icon: RejectedIcon,  gradient: 'linear-gradient(135deg, #F87171 0%, #EF4444 100%)' },
    ];
    const [activeTab, setActiveTab] = useState(0);
    const [editData, setEditData] = useState(null);

    const handleTabChange = (_, newValue) => {
        setActiveTab(newValue);
        if (newValue !== 1) setEditData(null);
    };

    const handleEdit = (timesheet) => {
        setEditData(timesheet);
        setActiveTab(1);
    };

    const handleFormDone = () => {
        setEditData(null);
        setActiveTab(0);
        fetchStats();
    };
    
    if(statsLoading) return <Loader />

    return (
        <Box sx={{ p: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                My Timesheets
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 2.5 }}>
                Log your work hours, track approvals, and manage weekly timesheets
            </Typography>

            <Box sx={{ bgcolor: '#fff', borderRadius: '12px', boxShadow: '0px 2px 4px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', p: 0.5, mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        '& .MuiTabs-indicator': { display: 'none' },
                        '& .MuiTab-root': {
                            textTransform: 'none', minWidth: 140, fontWeight: 600, color: '#64748b',
                            minHeight: '44px', borderRadius: '8px', marginRight: '4px',
                            '&.Mui-selected': { color: '#1976d2', bgcolor: '#e3f2fd' },
                            '&:hover': { bgcolor: '#f8fafc' },
                        },
                    }}
                >
                    <Tab icon={<AccessTimeIcon sx={{ fontSize: 19 }} />} iconPosition="start" label="My Timesheets" />
                    <Tab icon={<AddCircleOutlineIcon sx={{ fontSize: 19 }} />} iconPosition="start" label="Log Time" />
                </Tabs>
            </Box>

            {activeTab === 0 && (
                <>
                    <Grid container spacing={2.5} sx={{ mb: 3 }}>
                        {STAT_CARDS.map(card => (
                            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={card.label} sx={{ display: 'flex' }}>
                                <Card sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                                    <CardContent sx={{
                                        flexGrow: 1, background: card.gradient, display: 'flex', justifyContent: 'start', alignItems: 'center', gap: { xs: 1, sm: 1.5, lg: 2 },
                                        py: { xs: 1.5, sm: 2, lg: 2.5 }, px: { xs: 1.5, sm: 2, lg: 2.5 }, '&:last-child': { pb: { xs: 1.5, sm: 2, lg: 2.5 } },
                                    }}>
                                        <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 1, display: 'flex', color: '#fff', flexShrink: 0 }}>
                                            <card.icon />
                                        </Box>
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography sx={{ fontSize: { xs: 12, sm: 13 }, color: 'rgba(255,255,255,0.85)', fontWeight: 500, lineHeight: 1.3, wordBreak: 'break-word' }}>{card.label}</Typography>
                                            <Typography sx={{ fontSize: { xs: 20, sm: 22, md: 24, lg: 26 }, fontWeight: 700, color: '#fff', lineHeight: 1.2, wordBreak: 'break-word' }}>
                                                {card.value}
                                                {card.suffix && <Typography component="span" sx={{ fontSize: { xs: 12, sm: 13, lg: 14 }, fontWeight: 400, ml: 0.5 }}>{card.suffix}</Typography>}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                    <TimesheetList onEdit={handleEdit} />
                </>
            )}
            {activeTab === 1 && <TimesheetForm editData={editData} onDone={handleFormDone} />}
        </Box>
    );
}

export default MyTimesheet;
